<template>

    <div class="new-email-modal-form">
        <h3>MASSENEMAILS AN LEISTUNGSTRÄGER<template v-if="bulkGroups.length > 0"> — {{ bulkGroups.length }} EMAILS</template></h3>

        <bulk-provider-select
            v-bind:options="requestOptions"
        ></bulk-provider-select>

        <div class="bulk-separator"></div>

        <div class="input-wrapper">
            <label>Von:</label>
            <!-- Listen-Modus: Emails gehen standardmäßig vom Bearbeiter der jeweiligen Reise raus -->
            <multiselect
                v-if="isListMode"
                v-model="senderOption"
                v-bind:options="senderOptions"
                label="label"
                track-by="key"
                v-bind:allow-empty="false"
            />
            <email-select v-if="!isListMode || sender === 'me'" v-model="newEmail.fromEmail"/>
        </div>

        <div class="input-wrapper">
            <label>Vorlage:</label>
            <template-selection></template-selection>
        </div>

        <div class="input-wrapper">
            <label>Betreff:</label>
            <div
                ref="subjectEditor"
                class="input bulk-subject-input"
                contenteditable="true"
                spellcheck="false"
                v-on:input="onSubjectInput"
                v-on:keydown.enter.prevent
                v-on:dblclick="onSubjectDblclick"
                title="Änderungen gelten für alle Emails — graue Bausteine werden pro Empfänger automatisch befüllt (Doppelklick wandelt sie in normalen Text um)"
            ></div>
        </div>

        <div class="input-wrapper">
            <label>Beschreibung:</label>
            <tag-select
                v-model="tags"
                v-bind:option-tags="optionTags"
                my-class="is-medium"
            />
        </div>

        <div class="input-wrapper" v-if="requestOptions && requestOptions.statuses">
            <label>Status ändern:</label>
            <multiselect
                placeholder="nicht ändern"
                v-bind:options="requestOptions.statuses"
                label="value"
                track-by="key"
                v-model="status"
            />
        </div>

        <div class="input-wrapper">
            <label>Neue Aufgabe:</label>
            <input-date v-model="newEmail.todoDate"></input-date>
        </div>

        <div class="attachments">
            <label>Anhänge:</label>
            <attachments v-bind:ordersSelected="ordersSelected" v-model="newEmail._files"></attachments>
            <br>
        </div>

        <div class="pell-wrapper">
            <label>Email:</label>

            <div style="z-index:0">
                <div class="bulk-editor-nav">
                    <button class="button" v-bind:disabled="previewIndex <= 0 || isRendering" v-on:click.prevent="previewIndex--" title="Vorherige Email (Pfeil links)">
                        <i class="material-icons">chevron_left</i>
                    </button>
                    <button class="button" v-bind:disabled="previewIndex >= bulkGroups.length - 1 || isRendering" v-on:click.prevent="previewIndex++" title="Nächste Email (Pfeil rechts)">
                        <i class="material-icons">chevron_right</i>
                    </button>

                    <div v-if="currentGroup">
                        <b>Email {{ previewIndex + 1 }} / {{ bulkGroups.length }} — {{ currentGroup.provider.name }}</b>
                        <template v-if="currentContact"> an {{ currentContact.email }}</template>
                        <template v-if="isListMode && currentSender.fromEmail"> – von {{ currentSender.fromEmail.email }}</template>
                    </div>
                    <div v-else><b>Keine Leistungen ausgewählt</b></div>

                    <i
                        class="material-icons bulk-editor-info"
                        v-tooltip="'Änderungen gelten für alle Emails. Die grau hinterlegten Bausteine werden pro Empfänger automatisch befüllt — sie können gelöscht oder per Doppelklick in normalen (für alle gleichen) Text umgewandelt, aber nicht direkt bearbeitet werden. Mit ←/→ durch die Emails blättern.'"
                    >info_outline</i>
                </div>

                <loading v-if="isRendering"></loading>

                <div>
                    <div>
                        <email-editor v-model="editorHtml" @keyup="registerKeyUp" @dblclick="onEditorDblclick" />
                    </div>
                </div>
            </div>
        </div>

        <div class="input-wrapper"></div>
        <div class="buttons-wrapper is-flex is-justify-content-right">
            <div class="is-flex">
                <div>
                    <button
                        class="button is-primary is-large mr-0"
                        v-bind:class="{ 'is-loading' : isSending }"
                        v-bind:disabled="bulkGroups.length === 0"
                        v-on:click="send"
                        style="margin: 10px"
                    ><i class="material-icons">check</i> {{ sendButtonLabel }}</button>
                </div>
            </div>
        </div>
    </div>
</template>



<script>
import axios          from 'axios';
import jsonToFormData from 'json-form-data';
import debounce       from 'lodash/debounce';
import uniq           from 'lodash/uniq';

import Loading from '@components/Loading';
import { EmailEditor, Multiselect, InputDate } from '@components/form';
import { defaultEmailTags } from '@clientSpecific/utilities/defaultValues';
import { settings } from '@clientSpecific/utilities/settings';
import { notifyError, notifySuccess } from '@components/Notification';
import { apiErrorMessage } from '@utilities/api';
import { optionsHotelRequests } from '@api';
import { validateEmail } from '@utilities/functions';
import store from '../../store';
import {
    wrapTemplate,
    extractTemplate,
    freezeToken,
    markSubjectTemplate,
    parseMarkedSubject,
    subjectTwigParts
} from '../../bulkTemplateTokens';
import Attachments from './Attachments';
import BulkProviderSelect from './BulkProviderSelect';
import TemplateSelection from './TemplateSelection';
import TagSelect from '@components/form/TagSelect';
import EmailSelect from '@components/form/EmailSelect';

export default {
    store: store,
    props: {
        preselectedOrder: { default: null },
        bulkRequestIds:   { type: Array, default: null }, //Listen-Modus: genau diese Anfragen, reiseübergreifend
        attachDocument:   { type: Object, default: null },
        skipInit:         { type: Boolean, default: false } //true wenn das Form nur durch Modus-Wechsel neu erzeugt wurde
    },

    data: function () {
        return {
            settings: settings,

            optionTags: defaultEmailTags,
            tags: [],

            requestStatus: null,
            requestOptions: null,

            //Master-Template (roher Twig-Code inkl. Signatur) — Quelle für alle Emails
            template: '',
            //aktuell angezeigte, gerenderte Email (editierbar; Twig-Teile als Tokens)
            editorHtml: '',
            //Betreff-Editor zeigt den gerenderten Betreff; Platzhalter-Werte sind
            //nicht editierbare Bausteine (data-twig trägt den Twig-Code), der Rest
            //ist frei editierbar und wird als Template für alle übernommen
            subjectInternalEdit: false,
            lastRenderedKey: null,

            previewIndex: 0,
            isRendering: false,
            renderCancelSource: null,

            isSending: false,
            sendProgress: { current: 0, total: 0 },

            //Listen-Modus: 'orderUser' = Bearbeiter der jeweiligen Reise, 'me' = eigene Adresse
            sender: 'orderUser',

            //Eingabe-Generation: Render-Antworten, die älter sind als die letzte
            //Betreff-Eingabe, dürfen den Betreff-Editor nicht mehr überschreiben
            subjectEditGeneration: 0
        }
    },

    computed: {
        ordersSelected: function () {
            return this.$store.state.ordersSelected;
        },

        emailType: function () {
            return this.$store.state.emailType;
        },

        bulkGroups: function () {
            return this.$store.getters.bulkGroups;
        },

        bulkGroupContact: function () {
            return this.$store.getters.bulkGroupContact;
        },

        currentGroup: function () {
            return this.bulkGroups.length > 0 ? this.bulkGroups[Math.min(this.previewIndex, this.bulkGroups.length - 1)] : null;
        },

        currentContact: function () {
            return this.currentGroup ? this.bulkGroupContact(this.currentGroup) : null;
        },

        isListMode: function () {
            return this.$store.state.bulkListMode;
        },

        senderOptions: function () {
            return [
                { key: 'orderUser', label: 'Bearbeiter der jeweiligen Reise' },
                { key: 'me',        label: 'Meine Adresse' }
            ];
        },

        senderOption: {
            get: function () { return this.senderOptions.find(option => option.key === this.sender); },
            set: function (option) { this.sender = option.key; }
        },

        currentSender: function () {
            return this.currentGroup ? this.groupSender(this.currentGroup) : {};
        },

        newEmail: {
            get: function () {
                return this.$store.state.newEmail;
            },
            set: function (value) {
                this.$store.state.newEmail = value;
            }
        },

        //Rendering-Sprache (Auswahl-UI lebt in TemplateSelection)
        locale: function () {
            return this.$store.state.localeSelected;
        },

        status: {
            get() {
                return this.requestOptions.statuses.find(status => status.key === this.requestStatus)
            },
            set: function (status) {
                this.requestStatus = !!status ? status.key : null
            }
        },

        //identifiziert die aktuell anzuzeigende Email (Empfänger + Requests + Locale)
        previewKey: function () {
            if (!this.currentGroup) {
                return null;
            }
            const contact = this.currentContact;
            return this.currentGroup.feID +
                '|' + this.currentGroup.requests.map(request => request.id).join(',') +
                '|' + (contact ? (contact.id || contact.email) : 'none') +
                '|' + (this.currentSender.userId || 'me') +
                '|' + this.locale;
        },

        sendButtonLabel: function () {
            if (this.isSending) {
                return this.sendProgress.current + ' / ' + this.sendProgress.total + ' gesendet...';
            }
            return this.bulkGroups.length + ' Email' + (this.bulkGroups.length === 1 ? '' : 's') + ' senden';
        }
    },

    methods: {
        registerKeyUp() {
            this.$store.state.emailManuallyChanged = true;
        },

        //Betreff-Editor: statische Textknoten + nicht editierbare Platzhalter-Bausteine;
        //aus der DOM-Reihenfolge wird das Template rekonstruiert (gelöschter Baustein
        //= Platzhalter für alle entfernt)
        extractSubjectFromEditor() {
            const editor = this.$refs.subjectEditor;
            if (!editor) {
                return this.newEmail.subject || '';
            }

            let template = '';
            editor.childNodes.forEach(node => {
                if (node.nodeType === Node.TEXT_NODE) {
                    template += node.nodeValue;
                } else if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-twig')) {
                    template += node.getAttribute('data-twig');
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    //vom Browser eingefügte Elemente (z.B. beim Einfügen von Text)
                    template += node.textContent;
                }
            });
            return template.replace(/\n/g, ' ');
        },

        //Editor-Inhalt setzen: gerenderte Fassung (statics + Platzhalter-Bausteine) ...
        renderSubjectEditor(statics, values, twigs) {
            const editor = this.$refs.subjectEditor;
            if (!editor) {
                return;
            }

            editor.innerHTML = '';
            statics.forEach((staticPart, index) => {
                editor.appendChild(document.createTextNode(staticPart));
                if (index < values.length) {
                    const token = document.createElement('span');
                    token.className = 'bulk-twig-token';
                    token.setAttribute('contenteditable', 'false');
                    token.setAttribute('data-twig', twigs[index]);
                    token.setAttribute(
                        'title',
                        'Automatisch befüllt — pro Empfänger unterschiedlich. '
                            + 'Doppelklick wandelt den aktuellen Wert in normalen Text um (gilt dann für alle):\n'
                            + twigs[index]
                    );
                    token.textContent = values[index];
                    editor.appendChild(token);
                }
            });
        },

        //... oder als roher Template-Text (keine Auswahl / Rendering fehlgeschlagen)
        setSubjectEditorPlain(text) {
            const editor = this.$refs.subjectEditor;
            if (editor) {
                editor.textContent = text || '';
            }
        },

        onSubjectInput() {
            this.subjectInternalEdit = true;
            this.subjectEditGeneration += 1;
            this.newEmail.subject = this.extractSubjectFromEditor();
            this.$nextTick(() => {
                this.subjectInternalEdit = false;
            });
        },

        //Doppelklick auf einen Betreff-Baustein: gerenderten Wert als normalen Text
        //einfrieren — editierbar und (ohne Twig-Code) für alle Emails gleich
        onSubjectDblclick(event) {
            const token = event.target.closest ? event.target.closest('[data-twig]') : null;
            if (!token || this.isRendering || this.isSending) {
                return;
            }
            if (!confirm('Baustein in normalen Text umwandeln? Der aktuelle Wert gilt dann fest für alle Emails.')) {
                return;
            }

            token.parentNode.insertBefore(document.createTextNode(token.textContent), token);
            token.remove();
            this.onSubjectInput();
        },

        //Doppelklick auf einen Body-Baustein: gerenderten Inhalt als normalen Text/HTML
        //einfrieren; verschachtelte Bausteine darin bleiben dynamisch
        onEditorDblclick(event) {
            //Tabellen liegen im Shadow DOM des Editors: das eigentliche Ziel steht im composedPath
            const target = event.composedPath ? event.composedPath()[0] : event.target;
            const token = target && target.closest ? target.closest('[data-twig-id]') : null;
            if (!token || this.isRendering || this.isSending) {
                return;
            }
            if (!confirm('Baustein in normalen Text umwandeln? Der aktuelle Inhalt gilt dann fest für alle Emails.')) {
                return;
            }

            this.editorHtml = freezeToken(this.editorHtml, token.getAttribute('data-twig-id'));
            this.registerKeyUp();
        },

        getFullName: function (contact) {
            let name = contact.firstName || '';
            if (contact.lastName) {
                name += (name ? ' ' : '') + contact.lastName;
            }
            return name;
        },

        //Bearbeitungen aus dem Editor zurück ins Master-Template holen (Tokens → Twig-Code)
        syncTemplateFromEditor: function () {
            if (this.lastRenderedKey && this.editorHtml) {
                this.template = extractTemplate(this.editorHtml);
            }
        },

        //Absender einer Email. Listen-Modus + "Bearbeiter": Adresse des Reise-Bearbeiters bei der Organisation
        //der Reise (userId => Signatur des Bearbeiters in der Vorschau, userEmailId => Versand über sein Postfach);
        //hat er dort keine Adresse, bleibt es bei der eigenen
        groupSender: function (group) {
            if (this.isListMode && this.sender === 'orderUser') {
                const order = this.ordersSelected.find(item => item.id === group.orderID);
                const user = order && order.user ? this.$store.state.users.find(item => item.id === order.user.id) : null;
                const organisationId = order && order.organisationFilter ? order.organisationFilter.id : null;
                const userEmail = user ? (user.userEmails || []).find(email => email.organisation && email.organisation.id === organisationId) : null;
                if (userEmail) {
                    return { fromEmail: { email: userEmail.email, emailFrom: userEmail.emailFrom }, userId: user.id, userEmailId: userEmail.id };
                }
            }
            return { fromEmail: this.newEmail.fromEmail };
        },

        //Reisen einer Email: im Listen-Modus nur die Reise der Gruppe (sonst hinge jede Email an allen
        //ausgewählten Reisen und jeder Betreff trüge alle Reisenummern), sonst alle gewählten Vorgänge
        groupOrderIds: function (group) {
            if (this.$store.state.bulkListMode) {
                return uniq(group.requests.map(request => request.orderID));
            }
            return uniq(this.ordersSelected.map(order => order.id));
        },

        //rendert Betreff + Body für eine Provider-Gruppe über /api/email_preview (gleicher Endpoint wie Einzel-Flow);
        //markSubject=true umschließt Betreff-Platzhalter mit Markern (nur für die Editor-Ansicht, nie beim Versand);
        //contactOverride: eingefrorener Kontakt aus dem Versand-Loop
        renderFor: function (group, content, cancelToken = null, markSubject = false, contactOverride = null) {
            const contact = contactOverride || this.bulkGroupContact(group);

            let apiQuery = '?';
            const orderIds = this.groupOrderIds(group);
            if (orderIds.length > 0) {
                apiQuery += 'orders[]=' + orderIds.join('&orders[]=');
            }
            apiQuery += '&' + group.requests.map(request => 'requests[]=' + request.id).join('&');
            if (contact) {
                apiQuery += contact.id ? '&toContacts[]=' + contact.id : '&toContactsEmail[]=' + contact.email;
            }
            const sender = this.groupSender(group);
            if (sender.userId) {
                apiQuery += '&fromUser=' + sender.userId;
            }

            return axios.post(
                '/api/email_preview' + apiQuery,
                {
                    content: content,
                    subject: markSubject ? markSubjectTemplate(this.newEmail.subject) : this.newEmail.subject
                },
                {
                    headers: { 'X-LOCALE': this.locale },
                    ...cancelToken && { cancelToken }
                }
            ).then(response => {
                let responseContent = response.data.content;
                if (responseContent) {
                    responseContent = responseContent.replace(/\.\.\/\.\.\/clientSpecific\/email\//g, '/clientSpecific/email/');
                    responseContent = responseContent.replace(/\.\.\/clientSpecific\/email\//g, '/clientSpecific/email/');
                }
                return { content: responseContent, subject: response.data.subject };
            });
        },

        //rendert die Email des aktuellen Empfängers in den Editor (Twig-Teile als Tokens)
        renderCurrent: function () {
            if (this.isSending) {
                //während des Versand-Loops keine Preview-Renderings anstoßen
                return;
            }

            if (this.renderCancelSource) {
                this.renderCancelSource.cancel('Axios canceled');
            }

            if (!this.currentGroup || !this.template) {
                this.editorHtml = '';
                this.setSubjectEditorPlain(this.newEmail.subject);
                this.lastRenderedKey = null;
                this.isRendering = false;
                return;
            }

            const key = this.previewKey;
            const twigs = subjectTwigParts(this.newEmail.subject);
            const editGeneration = this.subjectEditGeneration;
            this.renderCancelSource = axios.CancelToken.source();
            this.isRendering = true;

            this.renderFor(this.currentGroup, wrapTemplate(this.template), this.renderCancelSource.token, true)
                .then(result => {
                    this.editorHtml = result.content;

                    //Betreff nur überschreiben, wenn seit Request-Start nicht getippt wurde
                    if (editGeneration === this.subjectEditGeneration) {
                        const parsedSubject = parseMarkedSubject(result.subject);
                        if (parsedSubject && parsedSubject.values.length === twigs.length) {
                            this.renderSubjectEditor(parsedSubject.statics, parsedSubject.values, twigs);
                        } else {
                            this.setSubjectEditorPlain(result.subject);
                        }
                    }

                    this.lastRenderedKey = key;
                    this.isRendering = false;
                }, error => {
                    if (!axios.isCancel(error)) {
                        this.isRendering = false;
                        notifyError('Die Email konnte nicht gerendert werden! Server Error!');
                    }
                });
        },

        buildPayload: function (group, rendered, contact) {
            const prepareContact = item => ({
                email: item.email.trim(),
                name:  this.getFullName(item)
            });

            const sender = this.groupSender(group);

            return {
                fromEmail:    sender.fromEmail,
                ...sender.userEmailId && { userEmailId: sender.userEmailId },
                toAddresses:  [prepareContact(contact)],
                ccAddresses:  (this.newEmail.ccAddresses  || []).filter(item => item.email).map(prepareContact),
                bccAddresses: (this.newEmail.bccAddresses || []).filter(item => item.email).map(prepareContact),
                subject:      rendered.subject ? rendered.subject.replace("\n", '') : rendered.subject,
                textHtml:     rendered.content,
                folder:       'sent',
                todoDate:     this.newEmail.todoDate,
                links:        this.groupOrderIds(group).map(id => ({ order: '/api/orders/' + id })),

                '_files':                this.newEmail._files.filter(item => !item.id),
                '_attachedDocumentIds':  this.newEmail._files.filter(item => item.id && !item.disposition).map(item => item.id),
                '_attachmentsForwarded': this.newEmail._files.filter(item => item.disposition),

                tags: this.tags.map(({ name }) => name),

                ...this.requestStatus && {
                    requestStatus: this.requestStatus,
                    requests:      group.requests.map(({ id }) => `/api/requests/${id}`)
                }
            };
        },

        validate: function () {
            if (this.bulkGroups.length === 0) {
                notifyError('Keine Leistungen ausgewählt!');
                return false;
            }

            if (!this.newEmail.subject) {
                notifyError('Der Betreff darf nicht leer sein!');
                return false;
            }

            if (!this.template) {
                notifyError('Die Nachricht darf nicht leer sein!');
                return false;
            }

            const missingContact = this.bulkGroups.filter(group => {
                const contact = this.bulkGroupContact(group);
                return !contact || !contact.email || !validateEmail(contact.email.trim());
            });

            if (missingContact.length > 0) {
                notifyError('Kein gültiger Email-Kontakt für: ' + missingContact.map(group => group.provider.name).join(', '));
                return false;
            }

            return true;
        },

        send: async function () {
            if (this.isSending) {
                return;
            }

            this.syncTemplateFromEditor();

            if (!this.validate()) {
                return;
            }

            //gleicher Storno-Check wie im Einzel-Flow
            const stornoCheckTag = settings.request?.stornoCheckTag;
            if (stornoCheckTag && this.requestStatus === 'canceled') {
                const hasStornoTag = this.tags.some(tag =>
                    tag.name && tag.name.toLowerCase().includes(stornoCheckTag.toLowerCase())
                );
                if (!hasStornoTag) {
                    if (!confirm('Keine Storno-Tag gefunden. Bist du sicher, dass du den Status ändern möchtest?')) {
                        return;
                    }
                }
            }

            const groups = this.bulkGroups;

            const providerCount = uniq(groups.map(group => (group.isAgency ? 'agency-' : '') + group.provider.id)).length;

            if (!confirm(groups.length + ' Emails an ' + providerCount + ' Leistungsträger senden?')) {
                return;
            }

            this.isSending = true;
            this.sendProgress = { current: 0, total: groups.length };

            //Kontakte beim Loop-Start einfrieren — Änderungen während des Versands
            //dürfen spätere Iterationen nicht unvalidiert beeinflussen
            const contactByGroup = new Map(groups.map(group => [group.feID, this.bulkGroupContact(group)]));

            const failed = [];

            for (const group of groups) {
                try {
                    //finales Rendering des rohen Templates (ohne Token-Wrapper) direkt vor dem Versand
                    const rendered = await this.renderFor(group, this.template, null, false, contactByGroup.get(group.feID));
                    const formData = jsonToFormData(this.buildPayload(group, rendered, contactByGroup.get(group.feID)));
                    await axios.post('/api/emails/send', formData, { skipErrorToast: true });

                    //erfolgreich versendete Requests abwählen, damit ein erneuter Versand nur die fehlgeschlagenen trifft
                    this.$store.state.requestsSelected = this.$store.state.requestsSelected.filter(
                        item => group.requests.findIndex(request => request.id === item.id) === -1
                    );
                    this.sendProgress.current++;
                } catch (error) {
                    failed.push(group.provider.name + ' (' + apiErrorMessage(error) + ')');
                }
            }

            this.isSending = false;

            const requestsChanged = !!this.requestStatus;

            if (failed.length === 0) {
                notifySuccess(this.sendProgress.current + ' Emails gesendet!');
                this.$emit('closeModal', requestsChanged);
            } else {
                notifyError(this.sendProgress.current + ' von ' + this.sendProgress.total + ' E-Mails gesendet. Nicht gesendet: ' + failed.join('; '));
            }
        },

        onKeyDown: function (event) {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                return;
            }

            const target = event.target;
            const tag = (target.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable ||
                (target.closest && target.closest('.multiselect'))
            ) {
                return;
            }

            if (event.key === 'ArrowLeft' && this.previewIndex > 0 && !this.isRendering) {
                this.previewIndex--;
            }
            if (event.key === 'ArrowRight' && this.previewIndex < this.bulkGroups.length - 1 && !this.isRendering) {
                this.previewIndex++;
            }
        }
    },

    created: function () {
        //vor dem Re-Rendern Bearbeitungen aus dem Editor sichern, sonst gingen sie verloren
        this.debouncedRenderCurrent = debounce(() => {
            this.syncTemplateFromEditor();
            this.renderCurrent();
        }, 800);

        optionsHotelRequests().then(response => {
            this.requestOptions = response.data;
        }, () => { notifyError('Couldn\'t load the Request Options'); });

        if (this.skipInit) {
            //Form wurde nur durch den Massenemail-Toggle neu erzeugt — Store ist bereits initialisiert
            return;
        }

        this.$store.state.isOpen = true;
        this.$store.state.isPhone = false;
        //Reste einer früheren Session dürfen den Vorlagen-Reload nicht per Confirm blockieren
        this.$store.state.emailManuallyChanged = false;

        this.$layout.toggleRightSideBar('AiChat');

        if (this.bulkRequestIds) {
            //Bearbeiter-Adressen für den Absender
            this.$store.dispatch('getUsers');
        }

        const init = this.bulkRequestIds
            ? this.$store.dispatch('loadBulkRequests', this.bulkRequestIds)
            : this.$store.dispatch('preselectOrders', { preselectedOrder: this.preselectedOrder, preselectedRequest: null });

        init.then(() => {
            this.$store.dispatch('resetEmailForm', { templateToSelect: null, contactToSelect: null, documentToAttach: this.attachDocument });
        });
    },

    mounted: function () {
        document.addEventListener('keydown', this.onKeyDown);

        //fillEmailBody kann schon gelaufen sein (Modus-Wechsel): vorhandene Vorlage übernehmen
        this.setSubjectEditorPlain(this.newEmail.subject);
        if (this.newEmail.textHtml) {
            this.template = this.newEmail.textHtml;
            this.renderCurrent();
        }
    },

    beforeDestroy: function () {
        document.removeEventListener('keydown', this.onKeyDown);
        if (this.renderCancelSource) {
            this.renderCancelSource.cancel('Axios canceled');
        }
        //Bearbeitungen zurück in den Store, damit sie einen Modus-Wechsel überleben
        //(bulk → provider → bulk verliert sonst alle Body-Änderungen)
        this.syncTemplateFromEditor();
        if (this.template) {
            this.$store.state.newEmail.textHtml = this.template;
        }
    },

    watch: {
        //fillEmailBody hat eine (neue) Vorlage geladen → als Master-Template übernehmen;
        //lastRenderedKey nullen, damit kein späterer Sync das neue Template mit dem
        //noch angezeigten ALTEN Editor-Inhalt überschreibt
        'newEmail.textHtml': function (value) {
            this.template = value || '';
            this.lastRenderedKey = null;
            this.renderCurrent();
        },

        //anderer Empfänger / andere Auswahl / anderer Kontakt → Bearbeitungen sichern, dann neu rendern
        previewKey: function () {
            if (this.isSending) {
                //Abwahl versendeter Requests während des Loops löst das hier aus — ignorieren
                return;
            }
            this.syncTemplateFromEditor();
            this.renderCurrent();
        },

        //Variante gewechselt (fillEmailBody setzt den rohen Vorlagen-Betreff) → neu rendern;
        //eigene Editor-Eingaben lösen KEIN Re-Rendering aus (würde den Cursor zurücksetzen)
        'newEmail.subject': function () {
            if (this.subjectInternalEdit) {
                return;
            }
            this.debouncedRenderCurrent();
        },

        bulkGroups: function () {
            if (this.previewIndex > this.bulkGroups.length - 1) {
                this.previewIndex = Math.max(0, this.bulkGroups.length - 1);
            }
        },

        '$store.state.variantSelected': function (variant) {
            this.tags = variant ?
                variant.tags.map(tag => {
                    return {
                        name: tag
                    }
                }) : []
        }
    },

    components: {
        InputDate,
        EmailSelect,
        TemplateSelection,
        TagSelect,
        Attachments,
        EmailEditor,
        Loading,
        Multiselect,
        BulkProviderSelect
    }
}
</script>
